import { AnyMessage, Message, MessageType } from '../message'
import { AnySourceStream, AnySubscription, Stream } from '../stream'
import { Pass } from './pass'

const scheduleMap = new Map<
    Stream,
    {
        queue: { message: AnyMessage; stream: Stream }[]
        subscription: AnySubscription
        subscribers: Set<Stream>
        connectedSubscribersCount: number
    }
>()

export class Schedule<V = any, E = any, C = any> extends Pass<V, E, C, V, E, C | undefined> {
    protected toFinish = false
    protected queue: { message: Message<V, E, C>; stream: Stream }[]

    protected signal: Stream

    constructor(signal: Stream, source?: AnySourceStream<V, E, C>) {
        super(source)

        this.signal = signal

        if (!scheduleMap.has(signal)) {
            this.setupSignal(signal)
        }

        const schedule = scheduleMap.get(signal)!
        schedule.subscribers.add(this)
        this.queue = schedule.queue
    }

    value(value: V) {
        if (this.toFinish) return
        this.queue.push({
            stream: this,
            message: {
                type: MessageType.Value,
                value: value,
            },
        })
    }

    error(error: E) {
        if (this.toFinish) return
        this.toFinish = true
        this.queue.push({
            stream: this,
            message: {
                type: MessageType.Error,
                error,
            },
        })
    }

    complete(complete: C) {
        if (this.toFinish) return
        this.toFinish = true
        this.queue.push({
            stream: this,
            message: {
                type: MessageType.Complete,
                complete,
            },
        })
    }

    connect() {
        super.connect()

        const schedule = scheduleMap.get(this.signal)!
        if (schedule.connectedSubscribersCount === 0) {
            this.signal.subscribe(schedule.subscription)
        }
        schedule.connectedSubscribersCount++
    }

    disconnect() {
        super.disconnect()

        const schedule = scheduleMap.get(this.signal)!
        schedule.connectedSubscribersCount--
        if (schedule.connectedSubscribersCount === 0) {
            this.signal.unsubscribe(schedule.subscription)
        }
    }

    protected close() {
        const subscribers = scheduleMap.get(this.signal)!.subscribers
        subscribers.delete(this)
        if (subscribers.size === 0) {
            this.disconnect()
        }
        super.close()
    }

    private setupSignal(signal: Stream) {
        const flush = () => {
            const queue = scheduleMap.get(signal)!.queue
            for (const { message, stream } of queue) {
                stream.internalPublish(message)
            }
            queue.length = 0
        }

        const complete = () => {
            flush()
            const subscribers = scheduleMap.get(signal)!.subscribers
            for (const sub of subscribers) {
                sub.complete(undefined)
            }
            scheduleMap.delete(signal)
        }

        const subscription = {
            value: () => {
                flush()
            },
            complete,
            error: complete,
        }

        scheduleMap.set(signal, {
            queue: [],
            subscription,
            connectedSubscribersCount: 0,
            subscribers: new Set(),
        })
    }
}

declare module '../stream' {
    interface Stream<SV = any, SE = any, SC = any, DV = SV, DE = SE, DC = SC> {
        schedule(signal: Stream): MirrorStream<DV, DE, DC>
    }
}

Stream.prototype.schedule = function <DV, DE, DC>(this: AnySourceStream<DV, DE, DC>, signal: Stream) {
    return new Schedule(signal, this) // tslint:disable-line:no-invalid-this
}
